/**
 * messages — the demo's typed HU/EN dictionary (no i18n library). Every
 * OPERATOR-FACING string of the demo surface lives here as `key → {hu, en}`;
 * the pass CONTENT itself (design seed field values persisted server-side)
 * stays as-authored and is deliberately NOT in this dictionary. Hungarian is
 * the default language, and the hu values are byte-identical to the previous
 * hardcoded copy so the existing Hungarian-assert tests keep passing.
 *
 * Kept React-free (plain data + `translateFor`) so the node-env vitest
 * modules (templateCatalog, mintPayloads) can translate without a renderer;
 * the React context/provider/hooks live in `./index`.
 */

export type Lang = 'hu' | 'en';

/** localStorage key for the persisted language choice. */
export const LANG_STORAGE_KEY = 'walletDemo.lang';

export const messages = {
  // ── Language toggle ────────────────────────────────────────────────────────
  'lang.switchAria': { hu: 'Nyelvváltás', en: 'Language' },

  // ── Layout (header nav + footer) ───────────────────────────────────────────
  'nav.passes': { hu: 'Pass-ok', en: 'Passes' },
  'nav.appleStudio': { hu: 'Apple stúdió', en: 'Apple Studio' },
  'nav.googleStudio': { hu: 'Google stúdió', en: 'Google Studio' },
  'nav.signOut': { hu: 'Kilépés', en: 'Sign out' },
  'nav.menu': { hu: 'Menü', en: 'Menu' },
  'nav.mobileMenu': { hu: 'Mobil menü', en: 'Mobile menu' },
  'footer.copyright': {
    hu: '© Insurwiz Technology Kft. — Wallet demo környezet',
    en: '© Insurwiz Technology Kft. — Wallet demo environment',
  },
  'footer.terms': { hu: 'Használati feltételek', en: 'Terms of use' },

  // ── Login ──────────────────────────────────────────────────────────────────
  'login.title': { hu: 'Bejelentkezés', en: 'Sign in' },
  'login.intro': {
    hu: 'Add meg az email címed — küldünk egy belépési linket. Nincs jelszó.',
    en: 'Enter your email address — we send you a sign-in link. No password.',
  },
  'login.sentTitle': { hu: 'Elküldtük a linket', en: 'Link sent' },
  'login.sentBody': {
    hu: 'Nyisd meg a leveled, kattints a linkre, és máris bent vagy. Ha 5 percen belül nem érkezik meg, ellenőrizd a spam mappát.',
    en: 'Open your inbox and click the link — you are in. If it does not arrive within 5 minutes, check your spam folder.',
  },
  'login.demoModeTitle': { hu: 'Demo mód:', en: 'Demo mode:' },
  'login.demoModeBody': {
    hu: 'ebben a környezetben nem megy ki email — lépj be ezzel a gombbal.',
    en: 'no e-mail is sent in this environment — sign in with this button.',
  },
  'login.demoModeSignIn': { hu: 'Belépés (demo mód)', en: 'Sign in (demo mode)' },
  'login.otherEmail': { hu: 'Másik email cím', en: 'Different email address' },
  'login.sessionExpired': {
    hu: 'A munkameneted lejárt — kérj új belépési linket.',
    en: 'Your session has expired — request a new sign-in link.',
  },
  'login.emailLabel': { hu: 'Email cím', en: 'Email address' },
  'login.emailPlaceholder': { hu: 'te@partner.hu', en: 'you@partner.com' },
  'login.submitting': { hu: 'Küldés…', en: 'Sending…' },
  'login.submit': { hu: 'Belépési link kérése', en: 'Request sign-in link' },
  'login.termsPrefix': {
    hu: 'Bejelentkezéssel elfogadod a demo környezetre vonatkozó',
    en: 'By signing in you accept the demo environment’s',
  },
  'login.termsLink': { hu: 'használati feltételeket', en: 'terms of use' },

  // ── Terms of use page (mounts outside Layout, like Login/Verify) ──────────
  'terms.backToLogin': { hu: '‹ Vissza a bejelentkezéshez', en: '‹ Back to sign-in' },
  'terms.backToPasses': { hu: '‹ Vissza a pass-okhoz', en: '‹ Back to passes' },

  // ── Verify (magic-link landing) ────────────────────────────────────────────
  'verify.missingToken': { hu: 'Hiányzó token a linkben', en: 'Missing token in the link' },
  'verify.failedTitle': { hu: 'Bejelentkezés sikertelen', en: 'Sign-in failed' },
  'verify.newLink': { hu: 'Új link kérése', en: 'Request a new link' },
  'verify.inProgress': { hu: 'Bejelentkezés…', en: 'Signing in…' },

  // ── Shared bits ────────────────────────────────────────────────────────────
  'common.loading': { hu: 'Betöltés…', en: 'Loading…' },
  'common.retry': { hu: 'Újrapróbálás', en: 'Try again' },
  'common.delete': { hu: 'Törlés', en: 'Delete' },
  'common.deleting': { hu: 'Törlés…', en: 'Deleting…' },
  'common.cancel': { hu: 'Mégse', en: 'Cancel' },

  // ── Dashboard ──────────────────────────────────────────────────────────────
  'dashboard.greeting': { hu: 'Üdv, {name}', en: 'Hi, {name}' },
  'dashboard.greetingFallback': { hu: 'Üdv!', en: 'there' },
  'dashboard.intro': {
    hu: 'Itt látod a demo során generált pass-okat. Új passt az Apple vagy a Google stúdióban készíthetsz.',
    en: 'These are the passes generated during the demo. Create a new pass in the Apple or Google studio.',
  },
  'dashboard.newApplePass': { hu: 'Új Apple pass', en: 'New Apple pass' },
  'dashboard.newGooglePass': { hu: 'Új Google pass', en: 'New Google pass' },
  'dashboard.th.recipient': { hu: 'Utas / Ügyfél', en: 'Traveller / Customer' },
  'dashboard.th.template': { hu: 'Sablon', en: 'Template' },
  'dashboard.th.details': { hu: 'Részletek', en: 'Details' },
  'dashboard.th.status': { hu: 'Státusz', en: 'Status' },
  'dashboard.th.expires': { hu: 'Lejár', en: 'Expires' },
  'dashboard.th.actions': { hu: 'Művelet', en: 'Actions' },
  'dashboard.open': { hu: 'Megnyitás', en: 'Open' },
  'dashboard.confirmDelete': { hu: 'Megerősít', en: 'Confirm' },
  'dashboard.emptyTitle': { hu: 'Még nincs pass', en: 'No passes yet' },
  'dashboard.emptyBody': {
    hu: 'Hozz létre egy demo pass-t — utazási vagy általános biztosítás sablonból —, hogy lásd, hogyan néz ki az Insurwiz Wallet a végfelhasználónál.',
    en: 'Create a demo pass — from the travel or general insurance template — to see what the Insurwiz Wallet looks like for the end user.',
  },

  // ── Template badges (Dashboard table) ──────────────────────────────────────
  'badge.travel': { hu: 'UTAZÁS', en: 'TRAVEL' },
  'badge.motorKotelezo': { hu: 'KGFB', en: 'MTPL' },
  'badge.motorCasco': { hu: 'CASCO', en: 'CASCO' },
  'badge.insurance': { hu: 'UTASBIZTOSÍTÁS', en: 'TRAVEL INSURANCE' },
  'badge.assistance': { hu: 'ÁLTALÁNOS', en: 'GENERAL' },
  'badge.lifeAccident': { hu: 'ÉLET & BALESET', en: 'LIFE & ACCIDENT' },

  // ── Live status pills (Dashboard + PassDetail) ─────────────────────────────
  'status.expired': { hu: 'LEJÁRT', en: 'EXPIRED' },
  'status.active': { hu: 'AKTÍV', en: 'ACTIVE' },
  'status.onTime': { hu: 'IDŐBEN', en: 'ON TIME' },
  'status.delayed': { hu: 'KÉSIK', en: 'DELAYED' },
  'status.delayedMinutes': { hu: '+{n} PERC', en: '+{n} MIN' },
  'status.delayedMinutesLong': { hu: '+{n} PERC KÉSÉS', en: '+{n} MIN DELAY' },
  'status.cancelled': { hu: 'TÖRÖLVE', en: 'CANCELLED' },
  'status.boarding': { hu: 'BESZÁLLÁS', en: 'BOARDING' },
  'status.departed': { hu: 'ELINDULT', en: 'DEPARTED' },
  'status.arrived': { hu: 'MEGÉRKEZETT', en: 'ARRIVED' },
  'status.diverted': { hu: 'ELTÉRÍTVE', en: 'DIVERTED' },

  // ── Template catalog (picker cards + titles) ───────────────────────────────
  'template.travel.title': { hu: 'Utazási biztosítás', en: 'Travel insurance' },
  'template.assistance.title': { hu: 'Általános biztosítás', en: 'General insurance' },
  'template.travel.description': {
    hu: 'Beszállókártya-elrendezés — élő járatkövetés járat megadásakor (opcionális).',
    en: 'Boarding-pass layout — live flight tracking when a flight is provided (optional).',
  },
  'template.assistance.description': {
    hu: 'Partner-brandelt asszisztencia kártya — hotline, hozzáférési időszak, link.',
    en: 'Partner-branded assistance card — hotline, access period, link.',
  },
  'template.liveBadge': { hu: 'Élő', en: 'Live' },

  // ── Studios (Apple + Google shared chrome) ─────────────────────────────────
  'studio.apple.title': { hu: 'Apple stúdió', en: 'Apple Studio' },
  'studio.google.title': { hu: 'Google stúdió', en: 'Google Studio' },
  'studio.pickTemplate': {
    hu: 'Válaszd ki, melyik sablonból indulsz.',
    en: 'Choose the template to start from.',
  },
  'studio.notEnabledHint': {
    hu: 'Nincs engedélyezve ehhez a fiókhoz',
    en: 'Not enabled for this account',
  },
  'studio.gate.none': {
    hu: 'Ehhez a fiókhoz egyik stúdió-sablon sincs engedélyezve (Utazási vagy Általános biztosítás).',
    en: 'No studio template is enabled for this account (Travel or General insurance).',
  },
  'studio.gate.template': {
    hu: 'Az {title} sablon nincs engedélyezve ehhez a fiókhoz.',
    en: 'The {title} template is not enabled for this account.',
  },
  'studio.gate.contactPrefix': {
    hu: 'A sablonokat az Insurwiz csapata kapcsolja be — írj a',
    en: 'Templates are enabled by the Insurwiz team — write to',
  },
  'studio.gate.contactSuffix': { hu: ' címre.', en: '.' },
  'studio.changeTemplate': { hu: '‹ Sablonváltás', en: '‹ Change template' },
  'studio.apple.subtitle': {
    hu: '{title} — tervezd meg és generáld le az Apple passt egy folyamatban.',
    en: '{title} — design and generate the Apple pass in one flow.',
  },
  'studio.google.subtitle.travel': {
    hu: 'Add meg a címzettet (a járat opcionális) — a Google Wallet pass azonnal elkészül.',
    en: 'Enter the recipient (the flight is optional) — the Google Wallet pass is generated instantly.',
  },
  'studio.google.subtitle.assistance': {
    hu: 'Add meg az ügyfelet és a szolgáltatás adatait — a Google Wallet pass azonnal elkészül.',
    en: 'Enter the customer and the service details — the Google Wallet pass is generated instantly.',
  },
  'studio.step1': { hu: 'Tervezés', en: 'Design' },
  'studio.step2': { hu: 'Címzett & generálás', en: 'Recipient & generate' },
  'studio.loadFailed': {
    hu: 'A betöltés nem sikerült — ellenőrizd a kapcsolatot, és próbáld újra.',
    en: 'Loading failed — check your connection and try again.',
  },
  'studio.advance': { hu: 'Tovább a generáláshoz →', en: 'Continue to generation →' },
  'studio.blockingHint': {
    hu: 'Előbb javítsd a pirossal jelölt hibákat — addig nem generálható pass.',
    en: 'Fix the errors marked in red first — no pass can be generated until then.',
  },
  'studio.saveFailedHint': {
    hu: 'A mentés nem sikerült — ellenőrizd a kapcsolatot, és próbáld újra.',
    en: 'Saving failed — check your connection and try again.',
  },

  // ── Step-1 canvas host (badges, save, smart section, popover titles) ───────
  'canvas.smartFeatures': { hu: 'Okos funkciók', en: 'Smart features' },
  'canvas.unsavedBadge': { hu: 'Nincs mentve (minta)', en: 'Not saved (sample)' },
  'canvas.savedBadge': { hu: 'Mentve', en: 'Saved' },
  'canvas.activeDesign': { hu: 'Aktív design', en: 'Active design' },
  'studio.step2PreviewHint': {
    hu: 'A mentett design (mintaadatokkal) — a pass a megadott utassal és járattal készül.',
    en: 'The saved design (with sample data) — the pass is made with the traveller and flight you enter.',
  },
  'canvas.activeDesignHint': {
    hu: 'Az aktív design kerül minden új passra — a Google stúdióban generáltakra is. A „Tovább a generáláshoz” menti és aktiválja.',
    en: 'The active design goes on every new pass — the Google studio’s too. “Continue to generate” saves and activates it.',
  },
  'canvas.saving': { hu: 'Mentés…', en: 'Saving…' },
  'canvas.save': { hu: 'Mentés', en: 'Save' },
  'canvas.saveFailedBadge': { hu: 'Mentés sikertelen', en: 'Save failed' },
  'popover.appearance': { hu: 'Megjelenés', en: 'Appearance' },
  'popover.barcode': { hu: 'Vonalkód', en: 'Barcode' },

  // ── Mint result cards (both studios) ───────────────────────────────────────
  'mint.done': { hu: 'Kész — a pass elkészült ✓', en: 'Done — your pass is ready ✓' },
  'mint.appleQrAria': { hu: 'Apple Wallet pass QR-kód', en: 'Apple Wallet pass QR code' },
  'mint.appleQrHint': {
    hu: 'Olvasd be a QR-kódot az iPhone kamerájával — a pass hozzáadódik az Apple Wallethez.',
    en: 'Scan the QR code with the iPhone camera — the pass is added to Apple Wallet.',
  },
  'mint.appleDesktopHintPrefix': { hu: 'A ', en: 'The ' },
  'mint.appleDesktopHintRest': {
    hu: ' csak iPhone-on adódik a Wallethez. Asztali gépen a lenti gomb csak letölti a fájlt (a hozzáadás nem történik meg) — ezért a QR a leggyorsabb út a demóhoz.',
    en: ' is added to the Wallet only on an iPhone. On desktop the button below just downloads the file (nothing is added) — so the QR is the fastest demo path.',
  },
  'mint.openPass': { hu: 'Pass megnyitása →', en: 'Open pass →' },
  'mint.doneFor': {
    hu: '{name} passa elkészült — tedd a telefonra az alábbi QR-kóddal vagy gombbal.',
    en: 'The pass for {name} is ready — put it on the phone with the QR code or button below.',
  },
  'mint.another': { hu: 'Új pass ugyanebből a sablonból', en: 'Another pass from this template' },
  'mint.googleQrAria': { hu: 'Google Wallet pass QR-kód', en: 'Google Wallet pass QR code' },
  'mint.googleQrHint': {
    hu: 'A QR-t Androidon olvasd be, vagy nyisd meg a linket a telefonon.',
    en: 'Scan the QR on Android, or open the link on the phone.',
  },
  'mint.addGoogleWallet': { hu: 'Hozzáadás a Google Wallethez', en: 'Add to Google Wallet' },
  'mint.appleTapHint': {
    hu: 'iPhone-on koppints a gombra — megnyílik a „Hozzáadás a Wallethez” lap.',
    en: 'On an iPhone, tap the button — the “Add to Wallet” sheet opens.',
  },
  'mint.googleTapHint': {
    hu: 'Androidon koppints a gombra — a Google Wallet átveszi a passt.',
    en: 'On Android, tap the button — Google Wallet takes the pass.',
  },

  // ── AppleWalletLink ────────────────────────────────────────────────────────
  'wallet.appleDownload': {
    hu: 'Letöltés / Hozzáadás Apple Wallet-hez',
    en: 'Download / Add to Apple Wallet',
  },
  'wallet.appleSoonTitle': {
    hu: 'Apple Wallet támogatás hamarosan',
    en: 'Apple Wallet support coming soon',
  },
  'wallet.appleSoon': { hu: 'Apple Wallet — Hamarosan', en: 'Apple Wallet — Coming soon' },
  'wallet.none': {
    hu: 'Ehhez a passhoz nincs Wallet-link.',
    en: 'This pass has no wallet link.',
  },

  // ── Shared mint-form clusters ──────────────────────────────────────────────
  'form.travelerName': { hu: 'Utas neve', en: 'Traveller name' },
  'form.travelerNamePlaceholder': { hu: 'Kovács Anna', en: 'Jane Smith' },
  'form.recipientEmail': { hu: 'Email cím', en: 'Email address' },
  // Honest: nothing is e-mailed at mint (SES only carries the sign-in link) —
  // the label used to promise 'a pass linket erre küldjük'.
  'form.recipientEmailHint': {
    hu: 'Csak nyilvántartásra — a demó nem küld levelet; a passt a QR-kóddal vagy a linkkel adod át.',
    en: 'For the record only — the demo sends no e-mail; hand the pass over with the QR code or the link.',
  },
  'form.addFlight': { hu: 'Járat hozzáadása (opcionális)', en: 'Add a flight (optional)' },
  'form.lookupDeparture': { hu: 'Indulás', en: 'Departure' },
  'form.lookupArrival': { hu: 'Érkezés', en: 'Arrival' },
  // ── Per-pass visibility window (spec 2026-08-31 Part A) ────────────────────
  'form.visibility.label': { hu: 'Kártya látható eddig', en: 'Card visible until' },
  'form.visibility.default': {
    hu: 'Alapértelmezett (érkezés + 6 óra)',
    en: 'Default (arrival + 6 hours)',
  },
  'form.visibility.h24': { hu: 'Érkezés + 24 óra', en: 'Arrival + 24 hours' },
  'form.visibility.d3': { hu: 'Érkezés + 3 nap', en: 'Arrival + 3 days' },
  'form.visibility.custom': { hu: 'Egyedi időpont', en: 'Custom date & time' },
  'form.visibility.hint': {
    hu: 'Eddig marad látható a kártya a Walletben — az élő frissítések ettől függetlenül futnak.',
    en: 'How long the card stays visible in Wallet — live updates keep flowing regardless.',
  },
  'form.flightOptionalHint': {
    hu: 'Élő járatkövetés — járat megadásakor. Járat nélkül a pass statikus marad (nincs élő státusz, gate / delay push).',
    en: 'Live flight tracking — when a flight is provided. Without a flight the pass stays static (no live status, gate / delay push).',
  },
  'form.minting': { hu: 'Pass készítése…', en: 'Creating pass…' },
  'form.mint': { hu: 'Pass létrehozása', en: 'Create pass' },
  'form.flightIata': { hu: 'Járatszám (IATA)', en: 'Flight number (IATA)' },
  'form.flightDate': { hu: 'Indulás napja', en: 'Departure date' },
  'form.lookupRetrying': { hu: 'Második próba…', en: 'Second attempt…' },
  'form.lookupPending': {
    hu: 'Keresés… (akár 30 mp új járatnál)',
    en: 'Searching… (up to 30 s for a new flight)',
  },
  'form.lookupCta': { hu: 'Járat ellenőrzése', en: 'Check flight' },
  'form.lookupBusyTitle': {
    hu: '⏳ A járat-keresés most lassú',
    en: '⏳ Flight lookup is slow right now',
  },
  'form.lookupBusyBody1': {
    hu: 'A háttérben felmelegítjük a járat adatait — várj 10-20 másodpercet és nyomd újra a',
    en: 'We are warming up the flight data in the background — wait 10–20 seconds and press',
  },
  'form.lookupBusyBody2': {
    hu: ' gombot. Vagy válassz az alábbi gyors járatok közül.',
    en: ' again. Or pick one of the fast flights below.',
  },
  'form.retryNow': { hu: 'Újrapróbálás most', en: 'Retry now' },
  'form.notFoundTitle': { hu: 'A járatot nem találtuk', en: 'Flight not found' },
  'form.notFoundBody': {
    hu: 'Próbáld újra ellenőrizni a járatszámot és a dátumot. Ha biztos vagy benne, válassz inkább egy gyors járatot az alábbi listából — a többi adatforrás nem mindig találja meg az új járatokat.',
    en: 'Double-check the flight number and the date. If you are sure they are right, pick a fast flight from the list below instead — the other data sources do not always find new flights.',
  },
  'form.passContent': { hu: 'Pass tartalom', en: 'Pass content' },
  'form.delayThreshold': { hu: 'Késés-küszöb (perc)', en: 'Delay threshold (minutes)' },
  'form.delayThresholdHint': {
    hu: 'Default 90 perc. Ha a járat ennél többet késik, a pass automatikusan frissül: megjelenik rajta a lounge/igénylés QR-kódja, és a telefon push-értesítést kap. Demo célra állítható 15–600 perc között.',
    en: 'Default 90 minutes. If the flight is delayed beyond this, the pass updates automatically: the lounge/claim QR code appears on it and the phone gets a push notification. For demo purposes it can be set between 15 and 600 minutes.',
  },
  'form.assistPhoneOptional': {
    hu: '24/7 asszisztencia telefonszám (opcionális)',
    en: '24/7 assistance phone number (optional)',
  },
  'form.assistPhoneHint': {
    hu: 'Megjelenik a pass-on, és kattintható (`tel:` link).',
    en: 'Shown on the pass, tappable (`tel:` link).',
  },
  'form.loungeOptional': { hu: 'Lounge belépés (opcionális)', en: 'Lounge access (optional)' },
  'form.loungePlaceholder': {
    hu: 'pl. DragonPass Menzies — automatikus 2h késés felett',
    en: 'e.g. DragonPass Menzies — automatic above a 2h delay',
  },
  'form.esimOptional': {
    hu: 'eSIM aktiválási link (opcionális)',
    en: 'eSIM activation link (optional)',
  },
  // General (assistance) template — insurer-facing wording: the pass renders
  // the same rows as Szolgáltatás / Azonosító / Érvényesség, so the form
  // labels match what the customer will see (no lounge / access-window copy).
  'form.assistanceSection': { hu: 'Szolgáltatás adatai', en: 'Service details' },
  'form.customerName': { hu: 'Ügyfél neve', en: 'Customer name' },
  'form.policyNumberOptional': {
    hu: 'Kötvényszám (opcionális)',
    en: 'Policy number (optional)',
  },
  'form.policyNumberPlaceholder': { hu: 'pl. KTV-2026-001234', en: 'e.g. POL-2026-001234' },
  'form.loungeName': { hu: 'Megnevezés', en: 'Name' },
  'form.loungeNamePlaceholder': {
    hu: 'pl. 24/7 Premium Asszisztencia',
    en: 'e.g. 24/7 Premium Assistance',
  },
  'form.loungeCode': { hu: 'Azonosító', en: 'Identifier' },
  'form.accessFrom': { hu: 'Érvényesség kezdete', en: 'Valid from' },
  'form.accessTo': { hu: 'Érvényesség vége', en: 'Valid until' },
  'form.hotline': { hu: '24/7 hotline', en: '24/7 hotline' },
  'form.relatedLinkOptional': { hu: 'Kapcsolódó link (opcionális)', en: 'Related link (optional)' },

  // ── zod validation messages (mintPayloads fragments) ───────────────────────
  'zod.nameRequired': { hu: 'Add meg a nevet', en: 'Enter the name' },
  'zod.emailInvalid': { hu: 'Érvényes email kell', en: 'A valid email is required' },
  'zod.flightExample': { hu: 'Pl. LH400', en: 'E.g. LH400' },
  'zod.tooLong': { hu: 'Túl hosszú', en: 'Too long' },
  'zod.lettersDigitsOnly': { hu: 'Csak betűk és számok', en: 'Letters and digits only' },
  'zod.dateFormat': { hu: 'YYYY-MM-DD formátum', en: 'YYYY-MM-DD format' },
  'zod.numberRequired': { hu: 'Szám kell', en: 'A number is required' },
  'zod.delayMin': { hu: 'Min. 15 perc', en: 'Min. 15 minutes' },
  'zod.delayMax': { hu: 'Max. 600 perc', en: 'Max. 600 minutes' },
  'zod.urlInvalid': { hu: 'Érvényes URL', en: 'A valid URL is required' },
  'zod.loungeNameRequired': { hu: 'Add meg a megnevezést', en: 'Enter the name' },
  'zod.loungeCodeRequired': { hu: 'Add meg az azonosítót', en: 'Enter the identifier' },
  'zod.hotlineRequired': {
    hu: 'Add meg a 24/7 hotline számát',
    en: 'Enter the 24/7 hotline number',
  },

  // ── PassDetail ─────────────────────────────────────────────────────────────
  'detail.back': { hu: '← Vissza', en: '← Back' },
  'detail.flight': { hu: 'Járat', en: 'Flight' },
  'detail.route': { hu: 'Útvonal', en: 'Route' },
  'detail.expires': { hu: 'Demo pass lejárata', en: 'Demo pass expires' },
  'detail.detailsTitle': { hu: 'Adatok', en: 'Details' },
  'detail.departure': { hu: 'Indulás', en: 'Departure' },
  'detail.arrival': { hu: 'Érkezés', en: 'Arrival' },
  'detail.visibleUntil': { hu: 'Walletben látható eddig', en: 'Visible in the wallet until' },
  'detail.notFoundTitle': { hu: 'A pass nem található', en: 'Pass not found' },
  'detail.notFoundBody': {
    hu: 'Lehet, hogy törölték, vagy a link elavult.',
    en: 'It may have been deleted, or the link is out of date.',
  },
  'detail.loadFailedTitle': { hu: 'Nem sikerült betölteni a passt', en: 'Could not load the pass' },
  'detail.toDashboard': { hu: 'Vissza a pass-okhoz', en: 'Back to passes' },
  'detail.previewTitle': { hu: 'Így néz ki a pass', en: 'How the pass looks' },
  'detail.previewHint': {
    hu: 'A sablon jelenlegi designja ennek a passnak az adataival.',
    en: 'The template’s current design with this pass’s data.',
  },
  'detail.actualDeparture': { hu: 'Tényleges indulás', en: 'Actual departure' },
  'detail.actualArrival': { hu: 'Tényleges érkezés', en: 'Actual arrival' },
  'detail.noLiveYet': {
    hu: 'Még nem érkezett élő járatadat — az első járatesemény után jelenik meg.',
    en: 'No live flight data yet — it appears after the first flight event.',
  },
  'detail.walletAddTitle': { hu: 'Hozzáadás a Wallethez', en: 'Add to the wallet' },
  'detail.walletAddBody': {
    hu: 'A linkek élesek: olvasd be a QR-kódot a telefonnal, vagy nyisd meg a linket a telefonon.',
    en: 'The links are live: scan the QR code with the phone, or open the link on the phone.',
  },
  'detail.confirmDelete': {
    hu: 'Biztos törlöd ezt a demo pass-t?',
    en: 'Delete this demo pass?',
  },
  'detail.deletePass': { hu: 'Pass törlése', en: 'Delete pass' },
  'detail.policyNumber': { hu: 'Kötvényszám', en: 'Policy number' },
  'detail.licensePlate': { hu: 'Rendszám', en: 'Licence plate' },
  'detail.validity': { hu: 'Érvényesség', en: 'Validity' },
  'detail.relatedLink': { hu: 'Kapcsolódó link', en: 'Related link' },
  'detail.liveStatus': { hu: 'Élő státusz', en: 'Live status' },
  'detail.delay': { hu: 'Késés', en: 'Delay' },
  'detail.gate': { hu: 'Kapu', en: 'Gate' },
  'detail.terminal': { hu: 'Terminál', en: 'Terminal' },
  'detail.delayMinutes': { hu: '+{n} perc', en: '+{n} min' },
  'detail.updatedAgo': {
    hu: 'Utolsó élő adat: {ago}',
    en: 'Last live update: {ago}',
  },
  'rel.justNow': { hu: 'most', en: 'just now' },
  'rel.minutesAgo': { hu: '{n} perce', en: '{n} min ago' },
  'rel.hoursAgo': { hu: '{n} órája', en: '{n} h ago' },
  'rel.daysAgo': { hu: '{n} napja', en: '{n} days ago' },

  // ── PassCanvas (direct-manipulation chrome) ────────────────────────────────
  'canvas.group.header': { hu: 'fejléc', en: 'header' },
  'canvas.group.primary': { hu: 'fő', en: 'primary' },
  'canvas.group.secondary': { hu: 'másodlagos', en: 'secondary' },
  'canvas.group.auxiliary': { hu: 'kiegészítő', en: 'auxiliary' },
  'canvas.group.back': { hu: 'hátoldal', en: 'back' },
  'canvas.liveDotTitle': {
    hu: 'Élő mező — automatikusan frissül',
    en: 'Live field — updates automatically',
  },
  'canvas.editFieldAria': { hu: '{title} mező szerkesztése', en: 'Edit {title} field' },
  'canvas.editNewFieldAria': { hu: 'Új mező szerkesztése', en: 'Edit new field' },
  'canvas.newField': { hu: 'Új mező', en: 'New field' },
  'canvas.editValueAria': { hu: '{title} érték szerkesztése', en: 'Edit {title} value' },
  'canvas.editNewValueAria': { hu: 'Új mező érték szerkesztése', en: 'Edit new field value' },
  'canvas.addFieldAria': { hu: 'Mező hozzáadása — {group}', en: 'Add field — {group}' },
  'canvas.chromeAria': { hu: 'Színek és logó szerkesztése', en: 'Edit colours and logo' },
  'canvas.editBarcodeAria': { hu: 'Vonalkód szerkesztése', en: 'Edit barcode' },
  'canvas.addBarcodeAria': { hu: 'Vonalkód hozzáadása', en: 'Add barcode' },
  'canvas.noBarcode': { hu: 'Nincs kód — koppints a hozzáadáshoz', en: 'No code — tap to add' },
  'canvas.viewBack': { hu: 'Hátoldal megtekintése ({n} sor) →', en: 'View back ({n} rows) →' },

  // ── ServiceURLsEditor ("Gombok és linkek") ─────────────────────────────────
  // Template-aware slots: the travel (boardingPass) canvas offers all six
  // Apple service URLs with the airline wording; the GENERAL (generic) canvas
  // offers only the two that make sense on an insurance pass — management and
  // a neutrally-worded contact link. hu values of the six travel labels are
  // byte-identical to the previous hardcoded copy.
  'serviceUrls.title': { hu: 'Gombok és linkek', en: 'Buttons and links' },
  'serviceUrls.help.travel': {
    hu: 'Kezelési és vásárlási linkek (pl. lounge-belépő, ülésválasztás), amiket a Wallet a passhoz kapcsol.',
    en: 'Management and purchase links (e.g. lounge access, seat selection) the Wallet attaches to the pass.',
  },
  'serviceUrls.help.generic': {
    hu: 'Kezelési és kapcsolatfelvételi linkek, amiket a Wallet a passhoz kapcsol.',
    en: 'Management and contact links the Wallet attaches to the pass.',
  },
  'serviceUrls.management': { hu: 'Kezelőfelület linkje', en: 'Management link' },
  'serviceUrls.purchaseLoungeAccess': {
    hu: 'Lounge-belépő vásárlás',
    en: 'Lounge access purchase',
  },
  'serviceUrls.purchaseSeatUpgrade': {
    hu: 'Ülésfejlesztés vásárlás',
    en: 'Seat upgrade purchase',
  },
  'serviceUrls.purchaseWiFi': { hu: 'Wi-Fi vásárlás', en: 'Wi-Fi purchase' },
  'serviceUrls.checkBaggage': { hu: 'Csomagfeladás', en: 'Baggage check-in' },
  'serviceUrls.contactAirline': {
    hu: 'Kapcsolat a légitársasággal',
    en: 'Contact the airline',
  },
  'serviceUrls.managePass': { hu: 'Pass kezelése', en: 'Manage pass' },
  'serviceUrls.contact': { hu: 'Kapcsolat', en: 'Contact' },

  // ── FieldInspector popover ─────────────────────────────────────────────────
  'inspector.title': { hu: 'Mező szerkesztése', en: 'Edit field' },
  'inspector.field': { hu: 'Mező', en: 'Field' },
  'inspector.liveBadge': { hu: 'élő mező', en: 'live field' },
  'inspector.label': { hu: 'Felirat', en: 'Label' },
  'inspector.value': { hu: 'Érték', en: 'Value' },
  'inspector.liveHint': {
    hu: 'Ez a mező automatikusan frissül a járat adataiból (a minta értéket látod) — a felirata a tiéd, az értékét a rendszer írja.',
    en: 'This field updates automatically from the flight data (you see the sample value) — the label is yours, the value is written by the system.',
  },
  'inspector.liveHintGeneric': {
    hu: 'Ez a mező automatikusan frissül a generáláskor megadott adatokból (a minta értéket látod) — a felirata a tiéd, az értékét a rendszer írja.',
    en: 'This field updates automatically from the data provided at generation (you see the sample value) — the label is yours, the value is written by the system.',
  },
  'inspector.alignment': { hu: 'Igazítás', en: 'Alignment' },
  'inspector.alignAuto': { hu: 'Automatikus', en: 'Automatic' },
  'inspector.alignLeft': { hu: 'Balra', en: 'Left' },
  'inspector.alignCenter': { hu: 'Középre', en: 'Centre' },
  'inspector.alignRight': { hu: 'Jobbra', en: 'Right' },
  'inspector.alignOsHint': {
    hu: 'Az iPhone néhány helyen felülbírálja (a fejléc mindig jobbra igazít).',
    en: 'iPhone overrides it in a few places (the header always right-aligns).',
  },
  'inspector.moveLeft': { hu: 'Mozgatás balra', en: 'Move left' },
  'inspector.moveRight': { hu: 'Mozgatás jobbra', en: 'Move right' },
  'inspector.deleteField': { hu: 'Mező törlése', en: 'Delete field' },

  // ── AddFieldMenu popover ───────────────────────────────────────────────────
  'addMenu.title': { hu: 'Mező hozzáadása', en: 'Add field' },
  'addMenu.liveHeading': {
    hu: 'Élő mezők (automatikusan frissülnek)',
    en: 'Live fields (update automatically)',
  },
  'addMenu.custom': { hu: 'Egyéni mező', en: 'Custom field' },
  'addMenu.customHint': {
    hu: 'Saját felirat és érték — nem frissül magától.',
    en: 'Your own label and value — does not update by itself.',
  },

  // ── BackOfPass ─────────────────────────────────────────────────────────────
  'back.front': { hu: '‹ Előlap', en: '‹ Front' },
  'back.title': { hu: 'Hátoldal', en: 'Back' },
  'back.empty': {
    hu: 'A hátoldal üres. Ide kerülhet elérhetőség, feltételek linkje vagy az élő Értesítés mező.',
    en: 'The back is empty. Contact details, a terms link, or the live Notice field can go here.',
  },
  'back.emptyGeneric': {
    hu: 'A hátoldal üres. Ide kerülhet elérhetőség, feltételek linkje vagy bármilyen kiegészítő információ.',
    en: 'The back is empty. Contact details, a terms link, or any additional information can go here.',
  },
  'back.newRowName': { hu: 'új sor', en: 'new row' },
  'back.rowLabelAria': { hu: '{row} felirat', en: '{row} label' },
  'back.rowValueAria': { hu: '{row} érték', en: '{row} value' },
  'back.rowDeleteAria': { hu: '{row} sor törlése', en: 'Delete {row} row' },
  'back.labelPlaceholder': { hu: 'Felirat', en: 'Label' },
  'back.liveBadge': { hu: 'élő', en: 'live' },
  'back.liveHint': {
    hu: 'Automatikusan frissül — az értékét a rendszer írja.',
    en: 'Updates automatically — the value is written by the system.',
  },
  'back.addRow': { hu: 'Új sor a hátoldalra', en: 'New row on the back' },

  // ── NotificationsPanel (push editor, spec 2026-08-31 Part B) ───────────────
  'notif.title': { hu: 'Értesítések', en: 'Notifications' },
  'notif.hint': {
    hu: 'A pass push-üzeneteinek időzítése és szövege. Helyettesítők a szövegben: {gate} = kapu, {jarat} = járatszám.',
    en: 'Timing and copy of the pass push messages. Placeholders: {gate} = gate, {jarat} = flight number.',
  },
  'notif.defaultBadge': { hu: 'Alapértelmezett terv', en: 'Default plan' },
  'notif.reset': { hu: 'Alapértelmezett visszaállítása', en: 'Reset to defaults' },
  'notif.enabled': { hu: 'Bekapcsolva', en: 'Enabled' },
  'notif.minutesBefore': { hu: 'Perccel indulás előtt', en: 'Minutes before departure' },
  'notif.header': { hu: 'Fejléc', en: 'Header' },
  'notif.body': { hu: 'Szöveg', en: 'Body' },
  'notif.t24h': { hu: 'Előzetes emlékeztető', en: 'Advance reminder' },
  'notif.t4h': { hu: 'Indulás-közeli emlékeztető', en: 'Near-departure reminder' },
  'notif.t30m': { hu: 'Beszállási emlékeztető', en: 'Boarding reminder' },
  'notif.delayHint': {
    hu: 'Ez az üzenet akkor megy ki, amikor a késés átlépi a generálásnál beállított küszöböt — az igénylési QR-kóddal együtt. Itt a {delay} = késés percben helyettesítő is él.',
    en: 'Sent the moment the delay crosses the threshold set at generation — together with the claim QR code. The {delay} = delay-in-minutes placeholder also works here.',
  },

  // ── API errors (lib/apiErrors.ts) ─────────────────────────────────────────
  'error.mintFailed': {
    hu: 'Nem sikerült létrehozni a passt. Próbáld újra — ha ismét előfordul, írj nekünk.',
    en: 'The pass could not be created. Please try again — if it happens again, contact us.',
  },
  'error.invalidRequest': {
    hu: 'Hiányos vagy hibás adat — ellenőrizd a kitöltött mezőket.',
    en: 'Missing or invalid data — check the fields you filled in.',
  },
  'error.templateDisabled': {
    hu: 'Ez a sablon nincs engedélyezve a fiókodban.',
    en: 'This template is not enabled for your account.',
  },
  'error.appleNotConfigured': {
    hu: 'Az Apple Wallet generálás ebben a környezetben nincs beállítva.',
    en: 'Apple Wallet generation is not configured in this environment.',
  },
  'error.appleTemplateNotSupported': {
    hu: 'Ehhez a sablonhoz még nem készíthető Apple pass.',
    en: 'This template cannot be issued as an Apple pass yet.',
  },
  'error.visibilityWindow': {
    hu: 'A láthatósági idő érvénytelen — egyedi időpontnak jövőbeli időt adj meg.',
    en: 'The visibility window is invalid — a custom time must be in the future.',
  },
  'error.policyNumber': {
    hu: 'A kötvényszám túl hosszú vagy hibás.',
    en: 'The policy number is too long or invalid.',
  },
  'error.transitNotSupported': {
    hu: 'Ehhez a passhoz járat kell a reptéri értesítésekhez.',
    en: 'This pass needs a flight for airport notifications.',
  },
  'error.flightNotFound': {
    hu: 'Nem találjuk a járatot ezen a napon — ellenőrizd a járatszámot és a dátumot.',
    en: 'We cannot find this flight on that day — check the flight number and the date.',
  },
  'error.flightBusy': {
    hu: 'A járatkereső most foglalt — próbáld újra néhány másodperc múlva.',
    en: 'The flight search is busy right now — try again in a few seconds.',
  },
  'error.notFound': {
    hu: 'A keresett elem nem található — lehet, hogy törölték.',
    en: 'The item was not found — it may have been deleted.',
  },
  'error.rateLimited': {
    hu: 'Túl sok próbálkozás — próbáld újra később.',
    en: 'Too many attempts — try again later.',
  },
  'error.invalidEmail': {
    hu: 'Adj meg egy érvényes email címet.',
    en: 'Enter a valid email address.',
  },
  'error.linkExpired': {
    hu: 'Ez a belépési link lejárt vagy már felhasználták — kérj újat.',
    en: 'This sign-in link has expired or was already used — request a new one.',
  },
  'error.sessionExpired': {
    hu: 'A munkameneted lejárt — jelentkezz be újra.',
    en: 'Your session has expired — sign in again.',
  },
  'error.tooLarge': {
    hu: 'A fájl túl nagy (legfeljebb 1 MB).',
    en: 'The file is too large (1 MB at most).',
  },
  'error.server': {
    hu: 'Átmeneti szerveroldali hiba — próbáld újra egy perc múlva.',
    en: 'A temporary server error — try again in a minute.',
  },
  'error.refused': {
    hu: 'A kérést a szerver elutasította ({code}).',
    en: 'The server refused the request ({code}).',
  },
  'error.network': {
    hu: 'Nincs kapcsolat a szerverrel — ellenőrizd az internetkapcsolatot.',
    en: 'Cannot reach the server — check your internet connection.',
  },
  'error.unknown': {
    hu: 'Váratlan hiba történt.',
    en: 'An unexpected error occurred.',
  },

  // ── AutomationMomentCard ───────────────────────────────────────────────────
  'moment.title': { hu: 'Késés-pillanat', en: 'Delay moment' },
  'moment.body': {
    hu: 'Ha a járat a küszöbnél többet késik (alapérték 90 perc — a generálásnál állítod), a pass magától frissül: megjelenik rajta a lounge/igénylés QR-kód, a kapu- és státuszmezők átíródnak, és a telefon push-értesítést kap.',
    en: 'If the flight is delayed beyond the threshold (default 90 minutes — set at generation), the pass updates itself: the lounge/claim QR code appears on it, the gate and status fields are rewritten, and the phone gets a push notification.',
  },
  // The GENERAL (no-flight) template's moment: the operator-update push. The
  // explanation leans on the existing changeMessage/notice mechanics — a
  // rewritten field's notification copy is what surfaces on the lock screen.
  'moment.updateTitle': { hu: 'Frissítés-pillanat', en: 'Update moment' },
  'moment.updateBody': {
    hu: 'Ha az operátor adatot módosít, a pass magától frissül: az érintett mezők átíródnak, és a telefon frissítő push-üzenetet kap — a zárolt képernyőn a mező beállított értesítő szövege jelenik meg.',
    en: 'When the operator changes the pass data, the pass updates itself: the affected fields are rewritten and the phone gets an update push message — the field’s notification text appears on the lock screen.',
  },
} as const satisfies Record<string, Record<Lang, string>>;

export type MessageKey = keyof typeof messages;

/** A bound translator: key (+ optional `{param}` values) → string. */
export type Translate = (key: MessageKey, params?: Record<string, string | number>) => string;

/**
 * Builds a translator for `lang`. Interpolates `{name}` placeholders from
 * `params` (a missing param leaves the placeholder verbatim — a visible bug
 * beats a silent empty string).
 */
export function translateFor(lang: Lang): Translate {
  return (key, params) => {
    const template = messages[key][lang];
    if (!params) return template;
    return template.replace(/\{(\w+)\}/g, (match, name: string) =>
      name in params ? String(params[name]) : match,
    );
  };
}

/** The hu fallback translator — module-level schema/catalog defaults use it. */
export const tHu: Translate = translateFor('hu');
